import { formatString } from '@websrc/App/Helpers/formatting'
import { useLocale } from '@websrc/App/Hooks/useLocale'
import { useSettings } from '@websrc/App/Hooks/useSettings'
import { useStaticText } from '@websrc/App/Hooks/useStaticText'
import { mapProductPage } from '@websrc/DataMappers/mapProductPage'
import clsx from 'clsx'
import { styles, typography } from 'leds'
import React, { MutableRefObject } from 'react'

import { AccordionGroup, AccordionItem } from '../../Accordion/AccordionGroup'
import Heading from '../../Heading/Heading'
import { ChokingHazard } from '../ChokingHazard/ChokingHazard'
import { StyledMarkdown } from '../ProductPageDetails.style'
import { ProductInfoBackorder } from './Backorder/ProductInfoBackorder'
import { ProductInfoButtons } from './Buttons/ProductInfoButtons'
import { ProductInfoCallouts } from './Callouts/ProductInfoCallouts'
import { ProductInfoCarousel } from './Carousel/ProductInfoCarousel'
import { CompatibleWithProduct } from './CompatibleWithProduct/CompatibleWithProduct'
import { ProductInfoHeader } from './Header/ProductInfoHeader'
import { ProductInfoPrice } from './Price/ProductInfoPrice'
import * as SC from './ProductInfo.style'
import { ProductInfoQuantity } from './Quantity/ProductInfoQuantity'

export interface ProductInfoProps {
  productDetails: ReturnType<typeof mapProductPage>['product_details']
  wrapperRef?: MutableRefObject<HTMLDivElement | null>
  quantity: number
  setQuantity: (quantity: number) => void
  warnings: string[]
  withCarousel?: boolean
  addToCartButtonRef: React.MutableRefObject<HTMLButtonElement | null>
  isTopOfPage?: boolean
}

type ProductWithUrl = Omit<
  ReturnType<typeof mapProductPage>['product_details']['compatible_with'][0],
  'url'
> & {
  url: string
}

const AccordionLabel = ({ text }: { text: string }) => (
  <Heading headline={text} level={2} size={'s'} style={{ paddingTop: 16 }} />
)

const getDefaultAccordionIndex = (productNumber: string): number => {
  let defaultAccordionIndex = 0

  // Overwrite the default accordion index for specific products
  // Needed to show temporary promotion code work around.
  const accordionIndexOverwrites: Record<string, number> = {
    '45401': 1,
    '45025': 1,
    '45400': 1,
    '45028': 1,
    '45005': 1,
    '45024': 1,
  }

  // If the product number is in the overwrite list, set the default index
  if (productNumber in accordionIndexOverwrites) {
    defaultAccordionIndex = accordionIndexOverwrites[productNumber]
  }

  return defaultAccordionIndex
}

// eslint-disable-next-line max-lines-per-function
export const ProductInfo = ({
  productDetails,
  quantity,
  setQuantity,
  wrapperRef,
  warnings,
  withCarousel,
  addToCartButtonRef,
  isTopOfPage,
}: ProductInfoProps) => {
  const t = useStaticText()
  const { grantsUrl } = useSettings()
  const locale = useLocale()
  const { number_of_students, callouts = [], product_number } = productDetails
  const accordionItems: AccordionItem[] = []

  if (callouts.length) {
    accordionItems.push({
      content: <ProductInfoCallouts callouts={callouts} hideTitle={true} />,
      label: <AccordionLabel text={t.ecom.callout_title} />,
      itemId: t.ecom.callout_title,
    })
  }

  if (productDetails.description) {
    accordionItems.push({
      label: <AccordionLabel text={t.ecom.product_description} />,
      content: (
        <>
          <StyledMarkdown markdown={productDetails.description} skipWidth={true} />
          {grantsUrl && t.ecom.grants_text && (
            <SC.GrantText>
              {t.ecom.grants_text}
              <SC.GrantLink href={`/${locale}${grantsUrl}`}>{t.ecom.grants_link_text}</SC.GrantLink>
            </SC.GrantText>
          )}
        </>
      ),
      itemId: t.ecom.product_description,
    })
  }

  const compatibleWithProducts = productDetails.compatible_with?.filter(
    (product): product is ProductWithUrl => !!product.url && !!product.name
  )

  if (compatibleWithProducts.length) {
    accordionItems.push({
      label: <AccordionLabel text={t.ecom.compatible_with} />,
      content: compatibleWithProducts.map((product) => (
        <CompatibleWithProduct
          image={product.thumbnail.thumbnail}
          key={product.name}
          name={product.name}
          url={product.url}
        />
      )),
      itemId: t.ecom.compatible_with,
    })
  }

  if (warnings.length > 0) {
    accordionItems.push({
      label: <AccordionLabel text={t.ecom.hazards} />,
      content: (
        <div className={styles({ display: 'flex', marginTop: 10 })}>
          {warnings.map((warning, i) => (
            <ChokingHazard key={i} warningText={warning} />
          ))}
        </div>
      ),
      itemId: t.ecom.hazards,
    })
  }

  let defaultAccordionIndex = getDefaultAccordionIndex(productDetails.product_number)

  return (
    <SC.Wrapper ref={wrapperRef}>
      <ProductInfoHeader
        isTopOfPage={isTopOfPage}
        overrideBrand={productDetails.override_brand}
        productNumber={product_number}
        show={true}
      />
      {withCarousel && <ProductInfoCarousel assets={productDetails.assets} />}
      <ProductInfoPrice
        className={styles({ marginBottom: 8 })}
        productDetails={productDetails}
        show={true}
      />
      <ProductInfoBackorder productDetails={productDetails} show={true} />

      {!productDetails.is_retired && (
        <>
          <div
            className={styles({
              display: { screenXS: 'block', screenM: 'flex' },
              alignItems: 'flex-start',
            })}
          >
            <ProductInfoQuantity quantity={quantity} setQuantity={setQuantity} show={true} />

            {!!number_of_students && (
              <p
                className={clsx(
                  typography({ size: 'md', color: 'grey' }),
                  styles({
                    display: { screenXS: 'block', screenM: 'none' },
                    marginTop: 10,
                    marginBottom: 16,
                  })
                )}
              >
                {formatString(t.ecom.number_of_students, number_of_students)}
              </p>
            )}
            <div className={styles({ flex: 'auto', marginBottom: 10 })}>
              <ProductInfoButtons
                addToCartButtonRef={addToCartButtonRef}
                color={'black'}
                colorMode={'light'}
                productDetails={productDetails}
                quantity={quantity}
                size={'medium'}
                type={'primary'}
              />
            </div>
          </div>

          {!!number_of_students && (
            <p
              className={clsx(
                typography({ size: 'md', color: 'grey' }),
                styles({ display: { screenXS: 'none', screenM: 'block' } })
              )}
            >
              {formatString(t.ecom.number_of_students, number_of_students)}
            </p>
          )}
        </>
      )}

      {productDetails.is_retired && (
        <>
          <ProductInfoButtons
            addToCartButtonRef={addToCartButtonRef}
            color={'black'}
            colorMode={'light'}
            productDetails={productDetails}
            quantity={quantity}
            size={'medium'}
            type={'secondary'}
          />
          <p className={clsx(styles({ marginY: 16 }), typography({ size: 'sm' }))}>
            {t.ecom.retired_info}
          </p>
        </>
      )}

      <div className={styles({ marginTop: 10 })}>
        <AccordionGroup initialOpenIndex={defaultAccordionIndex} items={accordionItems} />
      </div>
    </SC.Wrapper>
  )
}
